import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加认证token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // 处理认证失败：清 token 并跳登录页（对齐 services/api.ts，原跳 Landing 不一致）
      localStorage.removeItem('token');
      if (!window.location.pathname.startsWith('/auth/')) {
        window.location.href = '/auth/login';
      }
    }
    return Promise.reject(error);
  }
);

// 推荐域API接口
export const recommendationAPI = {
  // 健康检查
  healthCheck: () => api.get('/health'),
  
  // 策略管理
  getStrategies: () => api.get('/strategies'),
  getStrategyInfo: (strategyId: string) => api.get(`/strategies/${strategyId}`),
  generateRecommendations: (strategyId: string, data?: any) => 
    api.post(`/strategies/${strategyId}/recommendations`, data),
  generateBatchRecommendations: (data: any) => 
    api.post('/recommendations/batch', data),
  
  // 运行器管理
  getRunnerStatus: () => api.get('/runner/status'),
  addStrategyToRunner: (data: any) => api.post('/runner/strategies', data),
  removeStrategyFromRunner: (strategyId: string) => 
    api.delete(`/runner/strategies/${strategyId}`),
  startScheduler: () => api.post('/runner/scheduler/start'),
  stopScheduler: () => api.post('/runner/scheduler/stop'),
  
  // 历史数据
  getRecommendationHistory: (params?: any) => api.get('/history', { params }),
  
  // 因子管理
  getFactors: () => api.get('/factors'),
  getFactorInfo: (factorName: string) => api.get(`/factors/${factorName}`),
  
  // 模型管理
  getModels: () => api.get('/models'),
  getModelInfo: (modelId: string) => api.get(`/models/${modelId}`),
  
  // 配置管理
  validateConfig: () => api.get('/config/validate'),
  getConfigSummary: () => api.get('/config/summary'),
};

// 类型定义
export interface Recommendation {
  id: string;
  strategy_id: string;
  stock_code: string;
  stock_name: string;
  score: number;
  rank: number;
  factors: Record<string, number>;
  created_at: string;
}

export interface Strategy {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'error';
  performance_metrics: Record<string, number>;
  last_updated: string;
}

export interface Factor {
  name: string;
  description: string;
  category: string;
  available: boolean;
}

export interface Model {
  id: string;
  name: string;
  type: string;
  status: string;
  performance_metrics: Record<string, number>;
}

export interface RunnerStatus {
  is_running: boolean;
  active_strategies: string[];
  scheduled_jobs: Record<string, any>;
  last_updated: string;
}

export default api;
